
import React from 'react';
import CustomTooltip from '__common/components/CustomTooltip';
import ConfirmationSelectField from '__common/components/ConfirmationSelectField';
import { FormattedMessage } from 'react-intl';
import { Select as MuiSelect, MenuItem, FormControl } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const FIELD_TITLE = 'Are you sure you want to change the module specification?';
const FIELD_CONTENT = 'All array layouts will be lost.';

type Props = {
  field: selectBlock,
  index: number,
  value: string,
  updateProjectOption: (field: string, value: any) => void,
  error?: string,
  warn?: string,
  disable?: boolean,
};

export default class Select extends React.PureComponent<Props> {
  tooltipButton = () => {
    return null;
  }

  tooltip = () => {
    const { field } = this.props;
    if (!field.tooltip) return null;
    try {
      const content = typeof field.tooltip === 'function' ? field.tooltip() : field.tooltip;
      if (!content || typeof content !== 'string') return null;
      return (
        <CustomTooltip title={content} isHtml placement="right">
          <InfoOutlinedIcon className="dsp-field-tooltip-icon" />
        </CustomTooltip>
      );
    } catch {
      return null;
    }
  }

  renderSelectField = () => {
    const { field, value, updateProjectOption, error } = this.props;
    const isInvalid = Boolean(field.missing || error);
    
    // Use the value as-is, but provide empty string as fallback for undefined/null
    const safeValue = value || '';
    
    return (
      <FormControl fullWidth size="small" variant="standard" error={isInvalid}>
        <MuiSelect
          id={this.props.disable ? `${field.label} disabled-text` : `${field.label}`}
          value={safeValue}
          onChange={(event) => {
            updateProjectOption(field.apiField, event.target.value);
          }}
          disabled={this.props.disable ? this.props.disable : false}
          error={isInvalid}
          inputProps={{ 'aria-invalid': isInvalid || undefined }}
          displayEmpty
          MenuProps={{
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
            PaperProps: {
              sx: { zIndex: 100000 },
            },
          }}
          sx={{ '& .MuiSelect-select': { textAlign: 'left' } }}
        >
          {!safeValue && (
            <MenuItem value="">
              <em>Select an option</em>
            </MenuItem>
          )}
          {field.data && field.data.map((item, index) => (
            <MenuItem key={index} value={item.value}>{item.name}</MenuItem>
          ))}
        </MuiSelect>
      </FormControl>
    );

  }

  renderConfirmSelect = () => {
    const { field, value, updateProjectOption, error } = this.props;
    const isInvalid = Boolean(field.missing || error);

    return (
      <ConfirmationSelectField
        value={value || ''}
        items={field.data || []}
        onConfirm={(selectedValue) => {
          if (field.onConfirm && typeof field.onConfirm === 'function') {
            field.onConfirm(selectedValue);
          }
          updateProjectOption(field.apiField, selectedValue);
        }}
        title={field?.title?.length ? field.title : FIELD_TITLE}
        content={field?.content?.length ? field.content : FIELD_CONTENT}
        shouldConfirm={typeof field.shouldConfirm === 'function' ? field.shouldConfirm() : field.shouldConfirm}
        shouldConfirmCb={field.shouldConfirmCb}
        selectFieldProps={{
          lineDirection: 'center',
          type: 'text',
          fullWidth: true,
          itemLabel: 'name',
          itemValue: 'value',
          id: this.props.disable ? `${field.label}-disabled-text` : `${field.label}`,
        }}
        disabled={this.props.disable ? this.props.disable : false}
        error={isInvalid}
      />
    );
  }

  renderField = () => {
    const { field } = this.props;
    
    // Check if this field should show confirmation dialog
    const shouldConfirm = field.shouldConfirm && (
      typeof field.shouldConfirm === 'function' ? field.shouldConfirm() : field.shouldConfirm
    );
    
    if (shouldConfirm) {
      return this.renderConfirmSelect();
    }

    return this.renderSelectField();
  }

  render() {
    const { field, index, error, warn } = this.props;
    const labelContent = field.label ? <FormattedMessage id={field.label} /> : '';
    const tooltipIcon = this.tooltip();

    return (
      <div className="field" key={`select-${index}`}>
        {field.label && (
          tooltipIcon ? (
            <div className="dsp-label-row">
              <label id={field.id} className={`input-label ${field.missing || error ? 'input-warning' : ''}`}>
                {labelContent}
              </label>
              {tooltipIcon}
            </div>
          ) : (
            <label id={field.id} className={`input-label ${field.missing || error ? 'input-warning' : ''}`}>
              {labelContent}
            </label>
          )
        )}
        {this.renderField()}
        {field.note && field.note.shouldShow && field.note.message && <div className="note" dangerouslySetInnerHTML={{ __html: field.note.message}}></div>}
        {error && <div className="input-warning">{error}</div>}
        {warn && <div className="input-soft-warning">{warn}</div>}
      </div>
    );
  }
}
